/**
 * Constants shared by the recorder and by everything that has to reason about
 * what the recorder produced.
 */

/**
 * Age at which the recorder proactively replaces its streaming WebSocket
 * (#3293). On Cloud Run a WebSocket is an ordinary request subject to the
 * service's request timeout, so the platform closes it at exactly that age no
 * matter how much traffic flows over it. That timeout is 3600s — the Cloud Run
 * *maximum*, already raised there by #2839 — so it cannot be raised again, and
 * every recording past the hour was hard-killed mid-session (close code 1006,
 * no reason). The recorder therefore replaces the socket itself, with enough
 * margin left for a few retries before the deadline.
 *
 * A whisper streaming session owns exactly one transcript, so this is also the
 * length of every part but the last of a rotated recording. `useRecordingParts`
 * read it for exactly that reason until #3947 — a part had to have reached the
 * rotation age to be treated as continued — and no longer does: a reconnect
 * produces the same multi-transcript shape at an arbitrary age, so grouping on
 * this age recognised none of them. The recorder is its only reader now, and
 * this is the rotation age and nothing else.
 */
export const MAX_CONNECTION_AGE_MS = 55 * 60 * 1000

/**
 * Interval between IndexedDB flushes of buffered PCM audio, and therefore the
 * length of every chunk a recording writes but its last.
 *
 * It lives here because it has two readers that cannot see each other:
 * `useRecordingPersistence` paces its flush timer by it, and
 * `useSessionRecovery` multiplies a session's chunk count by it to say how long
 * a recoverable recording is. That second reader had its own copy of the number
 * — `10` seconds against a 5-second cadence — so the recovery dialog offered a
 * 20-minute consult as "40 minuten", which is the only thing distinguishing two
 * recoverable recordings from each other (#3869).
 *
 * Chunk count × this interval is the *audio* length rather than the wall-clock
 * span, which is what a behandelaar is picking on: a chunk is only written while
 * recording, so a pause adds elapsed time and no chunks. It is an estimate on
 * both ends, each bounded by one interval — the last chunk holds less than a
 * full interval (#3868), and the first can hold audio captured before `begin()`.
 * Against a duration shown in whole minutes that is noise.
 */
export const STORAGE_CHUNK_INTERVAL_MS = 5000
